import React, { useEffect, useState } from 'react';
import { activateToastRuntime } from './toastRuntime';

const TOAST_METHODS_WITH_OPTIONS = new Set(['toast', 'success', 'error', 'loading', 'custom']);

let hotToastLoadPromise = null;
let hotToastExports = null;
let deferredToastIdCounter = 0;

const queuedToastOperations = [];
const cancelledToastIds = new Set();
const hotToastExportListeners = new Set();

const notifyHotToastExportListeners = () => {
  hotToastExportListeners.forEach((listener) => {
    try {
      listener(hotToastExports);
    } catch (_error) {
      // no-op
    }
  });
};

const subscribeHotToastExports = (listener) => {
  if (typeof listener !== 'function') return () => {};
  hotToastExportListeners.add(listener);
  if (hotToastExports) {
    listener(hotToastExports);
  }
  return () => {
    hotToastExportListeners.delete(listener);
  };
};

const resolveHotToastExports = (moduleNamespace) => {
  if (!moduleNamespace || typeof moduleNamespace !== 'object') {
    return {};
  }

  if (typeof moduleNamespace.toast === 'function') {
    return {
      ...moduleNamespace,
      Toaster: moduleNamespace.Toaster || moduleNamespace.default?.Toaster,
      ToastBar: moduleNamespace.ToastBar || moduleNamespace.default?.ToastBar,
      resolveValue: moduleNamespace.resolveValue || moduleNamespace.default?.resolveValue,
    };
  }

  const defaultExport = moduleNamespace.default;
  if (defaultExport && typeof defaultExport === 'object' && typeof defaultExport.toast === 'function') {
    return {
      ...moduleNamespace,
      ...defaultExport,
      toast: defaultExport.toast || moduleNamespace.toast,
      Toaster: moduleNamespace.Toaster || defaultExport.Toaster,
      ToastBar: moduleNamespace.ToastBar || defaultExport.ToastBar,
      resolveValue: moduleNamespace.resolveValue || defaultExport.resolveValue,
    };
  }

  if (typeof defaultExport === 'function') {
    return {
      ...moduleNamespace,
      toast: defaultExport,
      Toaster: moduleNamespace.Toaster || defaultExport.Toaster,
      ToastBar: moduleNamespace.ToastBar || defaultExport.ToastBar,
      resolveValue: moduleNamespace.resolveValue || defaultExport.resolveValue,
    };
  }

  return moduleNamespace;
};

const createDeferredToastId = () => {
  deferredToastIdCounter += 1;
  return `deferred-toast-${deferredToastIdCounter}`;
};

const invokeToastMethod = (toastApi, methodName, args = []) => {
  if (typeof toastApi !== 'function') return undefined;

  if (methodName === 'toast') {
    return toastApi(...args);
  }

  const method = toastApi[methodName];
  if (typeof method !== 'function') return undefined;
  return method(...args);
};

const ensureDeferredToastId = (methodName, args = []) => {
  if (!TOAST_METHODS_WITH_OPTIONS.has(methodName)) return null;

  const existingOptions = args[1];
  if (existingOptions && typeof existingOptions === 'object' && !Array.isArray(existingOptions)) {
    if (existingOptions.id) {
      return existingOptions.id;
    }

    const nextId = createDeferredToastId();
    args[1] = { ...existingOptions, id: nextId };
    return nextId;
  }

  const nextId = createDeferredToastId();
  args[1] = { id: nextId };
  return nextId;
};

const flushQueuedToastOperations = () => {
  if (!hotToastExports || typeof hotToastExports.toast !== 'function') return;

  const toastApi = hotToastExports.toast;
  while (queuedToastOperations.length > 0) {
    const nextOperation = queuedToastOperations.shift();
    if (!nextOperation) continue;
    if (nextOperation.toastId && cancelledToastIds.has(nextOperation.toastId)) {
      continue;
    }

    try {
      nextOperation.invoke(toastApi);
    } catch (_error) {
      // no-op
    }
  }
};

const loadHotToastModule = async () => {
  if (hotToastExports) return hotToastExports;
  if (hotToastLoadPromise) return hotToastLoadPromise;

  hotToastLoadPromise = import(
    /* webpackChunkName: "react-hot-toast-runtime" */
    '../../node_modules/react-hot-toast/dist/index.js'
  )
    .then((moduleNamespace) => {
      hotToastExports = resolveHotToastExports(moduleNamespace);
      flushQueuedToastOperations();
      notifyHotToastExportListeners();
      return hotToastExports;
    })
    .catch((error) => {
      hotToastLoadPromise = null;
      throw error;
    });

  return hotToastLoadPromise;
};

const enqueueToastOperation = (operation) => {
  queuedToastOperations.push(operation);
  loadHotToastModule().catch(() => {
    // Keep queued operations for a future retry.
  });
};

const emitDeferredToast = (methodName, rawArgs = []) => {
  const args = [...rawArgs];
  activateToastRuntime();

  if (hotToastExports && typeof hotToastExports.toast === 'function') {
    return invokeToastMethod(hotToastExports.toast, methodName, args);
  }

  const deferredToastId = ensureDeferredToastId(methodName, args);
  enqueueToastOperation({
    toastId: deferredToastId,
    invoke: (toastApi) => invokeToastMethod(toastApi, methodName, args),
  });
  return deferredToastId;
};

const dismissDeferredToast = (toastId) => {
  if (hotToastExports && typeof hotToastExports.toast?.dismiss === 'function') {
    hotToastExports.toast.dismiss(toastId);
    return;
  }

  if (typeof toastId === 'undefined') {
    queuedToastOperations.length = 0;
    cancelledToastIds.clear();
    return;
  }

  cancelledToastIds.add(toastId);
};

const removeDeferredToast = (toastId) => {
  if (hotToastExports && typeof hotToastExports.toast?.remove === 'function') {
    hotToastExports.toast.remove(toastId);
    return;
  }

  if (typeof toastId === 'undefined') {
    queuedToastOperations.length = 0;
    cancelledToastIds.clear();
    return;
  }

  cancelledToastIds.add(toastId);
};

const emitDeferredPromiseToast = (promiseOrFactory, messages, options) => {
  activateToastRuntime();

  if (hotToastExports && typeof hotToastExports.toast?.promise === 'function') {
    return hotToastExports.toast.promise(promiseOrFactory, messages, options);
  }

  let resolvedPromiseInput = promiseOrFactory;
  if (typeof promiseOrFactory === 'function') {
    try {
      resolvedPromiseInput = promiseOrFactory();
    } catch (error) {
      resolvedPromiseInput = Promise.reject(error);
    }
  }

  enqueueToastOperation({
    invoke: (toastApi) => {
      if (typeof toastApi.promise === 'function') {
        toastApi.promise(resolvedPromiseInput, messages, options);
      }
    },
  });

  return resolvedPromiseInput;
};

const useHotToastExports = () => {
  const [loadedExports, setLoadedExports] = useState(() => hotToastExports);

  useEffect(() => {
    let mounted = true;
    const unsubscribe = subscribeHotToastExports((nextExports) => {
      if (!mounted) return;
      setLoadedExports(nextExports);
    });

    if (!hotToastExports) {
      loadHotToastModule().catch(() => {
        // no-op
      });
    }

    return () => {
      mounted = false;
      unsubscribe();
    };
  }, []);

  return loadedExports;
};

const toast = (...args) => emitDeferredToast('toast', args);
toast.success = (...args) => emitDeferredToast('success', args);
toast.error = (...args) => emitDeferredToast('error', args);
toast.loading = (...args) => emitDeferredToast('loading', args);
toast.custom = (...args) => emitDeferredToast('custom', args);
toast.promise = (promiseOrFactory, messages, options) => (
  emitDeferredPromiseToast(promiseOrFactory, messages, options)
);
toast.dismiss = dismissDeferredToast;
toast.remove = removeDeferredToast;

export const prewarmHotToast = () => (
  loadHotToastModule()
    .then(() => undefined)
    .catch(() => undefined)
);

export const Toaster = (props) => {
  const loadedExports = useHotToastExports();
  const RealToaster = loadedExports?.Toaster;
  if (typeof RealToaster !== 'function') return null;
  return <RealToaster {...props} />;
};

export const ToastBar = (props) => {
  const loadedExports = useHotToastExports();
  const RealToastBar = loadedExports?.ToastBar;
  if (typeof RealToastBar === 'function') {
    return <RealToastBar {...props} />;
  }

  const toastData = props?.toast;
  if (!toastData) return null;

  const messageResolver = loadedExports?.resolveValue;
  const resolvedMessage = typeof messageResolver === 'function'
    ? messageResolver(toastData.message, toastData)
    : (typeof toastData.message === 'function' ? toastData.message(toastData) : toastData.message);

  if (resolvedMessage == null) return null;

  const fallbackToastStyle = {
    display: 'flex',
    alignItems: 'center',
    background: '#fff',
    color: '#363636',
    lineHeight: 1.3,
    boxShadow: '0 3px 10px rgba(0, 0, 0, 0.1), 0 3px 3px rgba(0, 0, 0, 0.05)',
    maxWidth: '350px',
    pointerEvents: 'auto',
    padding: '8px 10px',
    borderRadius: '8px',
    ...(toastData.style || {}),
  };

  return (
    <div style={fallbackToastStyle}>
      <div style={{ margin: '4px 10px', flex: '1 1 auto', color: 'inherit' }} {...toastData.ariaProps}>
        {resolvedMessage}
      </div>
    </div>
  );
};

export { toast };
export default toast;
